import { useEffect, useState } from 'react'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts'
import { getComplianceTrends, type ComplianceTrendPoint } from '../lib/services/dashboards'
import { getReports, getReport } from '../lib/services/reports'
import { cmsStatusTone } from '../lib/services/scoringPresentation'
import { getFacilities, type FacilityOption } from '../lib/services/facilities'
import { ApiRequestError } from '../lib/errors'

function deltaIcon(delta: number) {
  if (delta > 0) return <TrendingUp size={14} className="text-emerald-600" />
  if (delta < 0) return <TrendingDown size={14} className="text-rose-600" />
  return <Minus size={14} className="text-slate-500" />
}

function monthLabel(dateValue: string): string {
  const parsed = new Date(dateValue)
  if (Number.isNaN(parsed.getTime())) return dateValue
  return parsed.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })
}

function statusRank(status: ComplianceTrendPoint['cms_outcome']): number {
  if (status === 'meets_standard') return 2
  if (status === 'conditional') return 1
  return 0
}

function scoreColor(score: number): string {
  if (score >= 95) return '#0f8b7f'
  if (score >= 85) return '#d97706'
  return '#e11d48'
}

function signedDelta(value: number): string {
  if (value > 0) return `+${value}`
  return `${value}`
}

function supportContextFromError(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const tracePart = error.traceId ? `trace=${error.traceId}` : 'trace=unavailable'
    const requestPart = error.requestId ? `request=${error.requestId}` : 'request=unavailable'
    return `${tracePart} ${requestPart}`
  }
  return 'trace=unavailable request=unavailable'
}

interface DomainBar { domain: string; label: string; count: number; total: number }

export function ComplianceTrendsPage() {
  const [windowDays, setWindowDays] = useState(365)
  const [facilityId, setFacilityId] = useState<string>('')
  const [facilities, setFacilities] = useState<FacilityOption[]>([])
  const [points, setPoints] = useState<ComplianceTrendPoint[]>([])
  const [domainBars, setDomainBars] = useState<DomainBar[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [supportContext, setSupportContext] = useState<string | null>(null)

  useEffect(() => {
    getFacilities().then(setFacilities).catch(() => {})
  }, [])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      setError(null)
      setSupportContext(null)
      getComplianceTrends(windowDays, facilityId || undefined)
        .then((rows) => {
          if (cancelled) return
          setPoints(rows)
        })
        .catch((loadError) => {
          if (cancelled) return
          setPoints([])
          setError('Trend data could not load. Retry the window or share the support context below.')
          setSupportContext(supportContextFromError(loadError))
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false)
        })
    }
    load()
    return () => { cancelled = true }
  }, [windowDays, facilityId])

  useEffect(() => {
    let cancelled = false
    getReports()
      .then((reports) => {
        const released = reports.filter((r) => r.status === 'released')
        if (released.length === 0 || cancelled) return
        released.sort((a, b) => b.generated_at.localeCompare(a.generated_at))
        return getReport(released[0].report_id)
      })
      .then((detail) => {
        if (cancelled || !detail?.report_payload) return
        const findings = Array.isArray((detail.report_payload as Record<string, unknown>).findings)
          ? ((detail.report_payload as Record<string, unknown>).findings as { domain?: string }[])
          : []
        const domainCounts = new Map<string, number>()
        for (const finding of findings) {
          const domain = finding.domain ?? 'OTHER'
          domainCounts.set(domain, (domainCounts.get(domain) ?? 0) + 1)
        }
        const labels: Record<string, string> = {
          IC: 'Infection Ctrl',
          SPD: 'Sterile Proc',
          ENV: 'Environment',
          LSC: 'Life Safety',
          MED: 'Medications',
          COMP: 'Compliance',
          WT: 'Waived Test',
          CR: 'Clinical Rec',
          HR: 'Human Res',
          EM: 'Emergency',
          ADA: 'Accessibility',
          WASTE: 'Med Waste',
        }
        const bars = Array.from(domainCounts.entries())
          .map(([domain, count]) => ({
            domain,
            label: labels[domain] ?? domain,
            count,
            total: findings.length,
          }))
          .sort((a, b) => b.count - a.count)
        setDomainBars(bars)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const latest = points[points.length - 1]
  const first = points[0]
  const previous = points[points.length - 2]
  const delta = latest && previous ? statusRank(latest.cms_outcome) - statusRank(previous.cms_outcome) : 0
  const scoreDelta = latest && first ? latest.overall_score - first.overall_score : 0
  const lowestPoint = points.reduce<ComplianceTrendPoint | null>((lowest, point) => {
    if (!lowest) return point
    return point.overall_score < lowest.overall_score ? point : lowest
  }, null)
  const attentionCount = points.filter((point) => point.cms_outcome !== 'meets_standard').length
  const latestStatus = latest ? latest.cms_outcome_label : 'N/A'
  const latestStatusTone = latest ? cmsStatusTone(latest.cms_outcome) : { text: 'text-slate-600', bg: 'bg-slate-100' }
  const chartData = points.map((point) => ({
    label: monthLabel(point.period_start),
    score: point.overall_score,
    outcome: point.cms_outcome,
  }))
  const canRenderResponsiveCharts = typeof window !== 'undefined' && 'ResizeObserver' in window
  const showEmptyState = !isLoading && !error && !latest

  return (
    <div className="mx-auto max-w-5xl space-y-5 pb-20">
      <section className="v11-workspace-header">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Compliance Analytics</p>
        <h1 className="mt-1 font-brand text-2xl font-bold text-[var(--brand-deep)]">Compliance Trends</h1>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2">
            {[90, 180, 365].map((window) => (
              <button
                key={window}
                className={windowDays === window ? 'tab-chip-active' : 'tab-chip'}
                onClick={() => setWindowDays(window)}
                type="button"
              >
                {window}d
              </button>
            ))}
          </div>
          {facilities.length > 1 && (
            <select
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700"
              value={facilityId}
              onChange={(event) => setFacilityId(event.target.value)}
            >
              <option value="">All Facilities</option>
              {facilities.map((facility) => (
                <option key={facility.facility_id} value={facility.facility_id}>{facility.facility_name}</option>
              ))}
            </select>
          )}
        </div>
      </section>

      {error && (
        <section className="v11-section border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <p className="font-semibold">{error}</p>
          {supportContext ? <p className="mt-2 text-xs font-semibold">Support context: {supportContext}</p> : null}
        </section>
      )}
      {isLoading && <section className="v11-section p-3 text-sm text-slate-600">Loading trend data...</section>}
      {showEmptyState && (
        <section className="v11-section border border-dashed border-slate-200 p-4 text-sm text-slate-600">
          No compliance trend data available yet. Released reports will appear here once scoring data is available.
        </section>
      )}

      {!isLoading && latest && (
        <>
          <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="v11-kpi-card">
              <div className="absolute left-0 right-0 top-0 h-1 rounded-t-2xl" style={{ background: scoreColor(latest.overall_score) }} />
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Current Status</p>
              <p className={`mt-1 inline-flex rounded-full px-3 py-1 text-lg font-extrabold ${latestStatusTone.bg} ${latestStatusTone.text}`}>{latestStatus}</p>
              <p className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-slate-600">
                {deltaIcon(delta)}
                {delta > 0 ? 'Improving' : delta < 0 ? 'Declining' : 'Stable'}
              </p>
            </div>
            <div className="v11-kpi-card v11-kpi-accent-teal">
              <div className="absolute left-0 right-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-[var(--brand-primary)] to-blue-400" />
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Latest Score</p>
              <p className="mt-1 font-brand text-3xl font-extrabold text-[var(--brand-deep)]">{latest.overall_score}</p>
              <p className="mt-2 text-xs text-slate-500">CMS deterministic scoring</p>
            </div>
            <div className="v11-kpi-card v11-kpi-accent-gold">
              <div className="absolute left-0 right-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-slate-300 to-slate-200" />
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Data Points</p>
              <p className="mt-1 font-brand text-3xl font-extrabold text-[var(--brand-deep)]">{points.length}</p>
              <p className="mt-2 text-xs text-slate-500">Released reports in window</p>
            </div>
          </section>

          <section className="v11-section p-5">
            <div className="v11-section-header">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-slate-500">Reviewer Data Story</p>
                <h2 className="mt-1 font-brand text-xl font-bold text-[var(--brand-deep)]">Trend readout</h2>
              </div>
              <p className="max-w-xl text-sm font-semibold leading-6 text-slate-600">
                Prioritize follow-up where scores remain below CMS thresholds or findings repeat by domain.
              </p>
            </div>
            <div className="grid gap-3 p-5 md:grid-cols-3">
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Window movement</p>
                <p className="mt-2 font-brand text-2xl font-extrabold text-[var(--brand-deep)]">
                  {points.length > 1 ? `${signedDelta(scoreDelta)} pts` : 'Pending'}
                </p>
                <p className="mt-1 text-xs font-semibold text-slate-500">
                  {points.length > 1 && first
                    ? `${monthLabel(first.period_start)} to ${monthLabel(latest.period_start)}`
                    : 'Needs another released report'}
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Lowest score</p>
                <p className="mt-2 font-brand text-2xl font-extrabold text-[var(--brand-deep)]">
                  {lowestPoint ? lowestPoint.overall_score : 'N/A'}
                </p>
                <p className="mt-1 text-xs font-semibold text-slate-500">
                  {lowestPoint ? monthLabel(lowestPoint.period_start) : 'No released report score'}
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Attention periods</p>
                <p className="mt-2 font-brand text-2xl font-extrabold text-[var(--brand-deep)]">{attentionCount}</p>
                <p className="mt-1 text-xs font-semibold text-slate-500">Conditional or at-risk reports</p>
              </div>
            </div>
          </section>

          <section className="v11-section p-5">
            <h2 className="mb-4 text-xs font-black uppercase tracking-widest text-slate-500">Score Over Time</h2>
            {chartData.length > 1 && canRenderResponsiveCharts ? (
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer minWidth={1} minHeight={1}>
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0f8b7f" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#0f8b7f" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: '#8ba4bc', fontWeight: 600 }}
                      axisLine={{ stroke: '#e2e8f0' }}
                      tickLine={false}
                    />
                    <YAxis
                      domain={[50, 100]}
                      tick={{ fontSize: 11, fill: '#8ba4bc', fontWeight: 600 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                      formatter={(value) => [`${value}`, 'Score']}
                    />
                    <ReferenceLine y={95} stroke="#0f8b7f" strokeDasharray="4 4" label={{ value: 'Meets Standard', position: 'right', fontSize: 10, fill: '#0f8b7f' }} />
                    <ReferenceLine y={85} stroke="#d97706" strokeDasharray="4 4" label={{ value: 'Conditional', position: 'right', fontSize: 10, fill: '#d97706' }} />
                    <Area
                      type="monotone"
                      dataKey="score"
                      stroke="#0f8b7f"
                      strokeWidth={2.5}
                      fill="url(#scoreGradient)"
                      dot={{ r: 4, fill: '#0f8b7f', stroke: 'white', strokeWidth: 2 }}
                      activeDot={{ r: 6, fill: '#0f8b7f', stroke: 'white', strokeWidth: 2, cursor: 'pointer' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-600">
                {chartData.length > 1
                  ? 'The score chart is unavailable in this browser, but the trend readout remains available above.'
                  : 'One data point is available. The score chart appears after another released report.'}
              </div>
            )}
          </section>

          {domainBars.length > 0 && (
            <section className="v11-section p-5">
              <h2 className="mb-4 text-xs font-black uppercase tracking-widest text-slate-500">
                Domain Breakdown (Latest Assessment)
              </h2>
              {canRenderResponsiveCharts ? (
                <div style={{ width: '100%', height: Math.max(200, domainBars.length * 36) }}>
                  <ResponsiveContainer minWidth={1} minHeight={1}>
                    <BarChart data={domainBars} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#8ba4bc', fontWeight: 600 }} axisLine={false} tickLine={false} />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={100}
                        tick={{ fontSize: 11, fill: '#475569', fontWeight: 600 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 600 }}
                        formatter={(value, _name, entry) => {
                          const count = Number(value)
                          const domain = (entry?.payload as DomainBar | undefined)?.domain ?? ''
                          return [`${count} finding${count === 1 ? '' : 's'} (${domain})`, 'Count']
                        }}
                      />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={24}>
                        {domainBars.map((entry) => (
                          <Cell
                            key={entry.domain}
                            fill={entry.count >= 3 ? '#e11d48' : entry.count >= 2 ? '#d97706' : '#0f8b7f'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : null}
              <div className="mt-4 flex flex-wrap gap-2" aria-label="Top finding domains">
                {domainBars.slice(0, 5).map((entry) => (
                  <span key={entry.domain} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600">
                    {entry.label}: {entry.count}
                  </span>
                ))}
              </div>
            </section>
          )}
          {domainBars.length === 0 && (
            <section className="v11-section border border-dashed border-slate-200 p-4 text-sm text-slate-600">
              No domain findings were included in the latest released report. Domain breakdown will appear when findings are present.
            </section>
          )}

          <section className="v11-section p-4">
            <h2 className="mb-3 text-xs font-black uppercase tracking-widest text-slate-500">Recent Assessments</h2>
            <div className="flex flex-wrap gap-2">
              {points.slice(-8).map((point, index) => {
                const tone = cmsStatusTone(point.cms_outcome)
                return (
                  <div key={`${point.period_start}-${index}`} className="space-y-1 text-center">
                    <div className={`rounded-lg px-3 py-2 text-xs font-bold ${tone.bg} ${tone.text}`}>
                      {point.overall_score}
                    </div>
                    <p className="text-[10px] font-semibold text-slate-400">{monthLabel(point.period_start)}</p>
                  </div>
                )
              })}
            </div>
          </section>
        </>
      )}

      {!isLoading && !error && !latest && !showEmptyState && (
        <section className="v11-section p-4 text-sm text-slate-600">No released trend data is available for this window.</section>
      )}
    </div>
  )
}
